import Web3 from "web3";
import store from "@/store";
import { contractInstanceInit } from "@/contract";
import { TOKENPOCKET_ICON } from "@/utils/walletCatalog";

const TOKENPOCKET_LEGACY = {
  name: "TokenPocket",
  rdns: "xyz.tp",
  icon: TOKENPOCKET_ICON,
};
class WalletManager {
  constructor() {
    this.providers = new Map();
    this.web3Instances = new Map();
    this.currentWallet = null;
  }

  init() {
    // 监听EIP-6963钱包宣布事件
    window.addEventListener(
      "eip6963:announceProvider",
      this.handleNewProvider
    );

    try {
      window.dispatchEvent(new Event("eip6963:requestProvider"));
    } catch (err) {
      console.warn("requestProvider 调用失败，使用传统注入方式", err);
    }

    this.addLegacyProvider();
  }

  handleNewProvider = (event) => {
    const { info, provider } = event.detail;
    const isExisting = this.providers.has(info.uuid);
    this.providers.set(info.uuid, { info, provider });
    console.log(`发现钱包: ${info.name}`, info);
    if (!isExisting) {
      window.dispatchEvent(new Event("walletManager:providersChanged"));
    }
  };

  addLegacyProvider() {
    const providers = [];

    const tpMobileProvider = window.tokenpocket?.ethereum;
    if (tpMobileProvider) {
      providers.push({
        info: {
          uuid: "legacy-tokenpocket-mobile",
          ...TOKENPOCKET_LEGACY,
        },
        provider: tpMobileProvider,
      });
    }

    if (window.ethereum?.isTokenPocket) {
      providers.push({
        info: {
          uuid: "legacy-tokenpocket-extension",
          ...TOKENPOCKET_LEGACY,
        },
        provider: window.ethereum,
      });
    } else if (window.ethereum?.isMetaMask) {
      providers.push({
        info: {
          uuid: "legacy-injected",
          name: "MetaMask",
          rdns: "io.metamask",
          icon: "",
        },
        provider: window.ethereum,
      });
    } else if (window.ethereum) {
      providers.push({
        info: {
          uuid: "legacy-injected",
          name: "Injected Provider",
          rdns: "unknown",
          icon: "",
        },
        provider: window.ethereum,
      });
    }
  
    if (window.gatewallet) {
      providers.push({
        info: {
          uuid:
            "0c1692a0-45c2-42a2-9e1a-d12203a0d51cc2b09bbd-e061-405a-a021-13d996f61f1c",
          name: "Gate Wallet",
          rdns: "io.gate.wallet",
          icon:
            "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJDSURBVHgB7Zq9jtpAEMfHlhEgQLiioXEkoAGECwoKxMcTRHmC5E3IoyRPkPAEkI7unJYmTgEFTYwA8a3NTKScLnCHN6c9r1e3P2llWQy7M/s1Gv1twCP0ej37dDq9x+Zut1t3t9vZjDEHIiSRSPg4ZpDL5fxkMvn1cDh8m0wmfugfO53OoFQq/crn8wxfY9EymQyrVCqMfHvScZx1p9ls3pFxXBy/bKlUipGPrVbLuQqAfsCliq3zl0H84zwtjQrOw4Mt1W63P5LvBm2d+Xz+YzqdgkqUy+WgWCy+Mc/nc282m4FqLBYL+3g8fjDxenq72WxANZbLJeA13zDX67UDioL5ybXwafMYu64Ltn3bdDweQ5R97fd7GyhBQMipx4POeEDHIu2LfDdBIGGz+hJ9CQ1ABjoA2egAZPM6AgiCAEQhsi/C4jHyPA/6/f5NG3Ks2+3CYDC4aTccDrn6ojG54MnEvG00GoVmWLIRNZ7wTCwDHYBsdACy0QHIhiuRETxlICWpMMhGZHmqS8qH6JLyGegAZKMDkI0uKf8X4SWlaZo+Pp1bRrwlJU8ZKLIvUjKh0WiQ3sRUbNVq9c5Ebew7KEo2m/1p4jJ4qAmDaqDQBzj5XyiAT4VCQezJigAU+IDU+z8vJFnGWeC+bKQV/5VZ71FV6L7PA3gg3tXrdQ+DgLhC+75Wq3no69P3MC0NFQpx2lL04Ql9gHK1bRDjsSBIvScBnDTk1WrlGIZBorIDEYJj+rhdgnQ67VmWRe0zlplXl81vcyEt0rSoYDUAAAAASUVORK5CYII=",
        },
        provider: window.gatewallet,
      });
    }
  
    let hasNewProvider = false;
    providers.forEach((item) => {
      if (!this.providers.has(item.info.uuid)) {
        this.providers.set(item.info.uuid, item);
        hasNewProvider = true;
      }
    });

    if (hasNewProvider) {
      window.dispatchEvent(new Event("walletManager:providersChanged"));
    }
  }

  async connectWallet(uuid) {
    const wallet = this.providers.get(uuid);
    if (!wallet) throw new Error("Wallet not found");

    try {
      console.log(wallet);
      const accounts = await wallet.provider.request({
        method: "eth_requestAccounts",
      });
      const web3 = new Web3(wallet.provider);
      const account = accounts[0];
      this.web3Instances.set(uuid, web3);
      this.currentWallet = { uuid, address: account, info: wallet.info };
      store.commit("SET_ADDRESS", account);
      await contractInstanceInit(web3, account);
      this.setupEventListeners(wallet.provider, uuid);
      location.reload();
      return this.currentWallet;
    } catch (error) {
      console.error("连接钱包失败:", error);
      throw error;
    }
  }

  async checkIfConnected() {
    return new Promise((resolve) => {
      const newProviders = Array.from(this.providers);
      if (newProviders.length) {
        const promiseList = [];
        for (let i = 0; i < newProviders.length; i++) {
          let wallet = newProviders[i][1];
          let uuid = newProviders[i][0];
          let provider = wallet.provider;
          const p = new Promise((resolve) => {
            provider
              .request({
                method: "eth_accounts",
              })
              .then((accounts) => {
                resolve({
                  accounts: accounts,
                  provider: newProviders[i],
                });
              });
          });
          promiseList.push(p);
        }
        Promise.all(promiseList).then((res) => {
          let loginAccountArr = res.filter((v) => v.accounts.length);
          if (loginAccountArr.length) {
            let loginAccount = loginAccountArr[0];
            let wallet = loginAccount.provider[1];
            let uuid = loginAccount.provider[0];
            let provider = wallet.provider;
            let account = loginAccount.accounts[0];
            const web3js = new Web3(provider);
            this.web3Instances.set(uuid, web3js);
            this.currentWallet = {
              uuid,
              address: account,
              info: wallet.info,
            };
            store.commit("SET_ADDRESS", account);
            contractInstanceInit(web3js, account);
            this.setupEventListeners(provider, uuid);
          } else {
            store.commit("CLEAR_ADDRESS");
          }
          resolve();
        });
      }
    });
  }

  setupEventListeners(provider, uuid) {
    provider.on("accountsChanged", (accounts) => {
      // console.log('object');
      // if (accounts.length === 0) {
      //   this.disconnectWallet(uuid);
      // } else {
      //   this.currentWallet.address = accounts[0];
      // }
      location.reload()
    });

    provider.on("chainChanged", (chainId) => {
      console.log("链已切换:", chainId);
      window.location.reload(); // 通常建议刷新页面
    });

    provider.on("disconnect", (error) => {
      console.log("钱包断开连接:", error);
      //   this.disconnectWallet(uuid);
    });
  }

  async disconnectWallet(uuid) {
    // this.web3Instances.delete(uuid);
    // if (this.currentWallet?.uuid === uuid) {
    //   this.currentWallet = null;
    // }
    const wallet = this.providers.get(uuid);
    // 根据钱包类型特殊处理
    switch (wallet.info.rdns) {
      case "io.metamask":
        // MetaMask没有真正的断开连接，只能重置账户
        await wallet.provider.request({
          method: "wallet_revokePermissions",
          params: [{ eth_accounts: {} }], // 特殊参数重置
        });
        break;

      case "com.coinbase.wallet":
        // Coinbase Wallet使用特定方法
        await wallet.provider.disconnect();
        break;

      case "xyz.tp":
      case "pro.tokenpocket":
        if (wallet.provider.disconnect) {
          await wallet.provider.disconnect();
        }
        break;

      default:
        // 默认尝试标准方法
        if (wallet.provider.disconnect) {
          await wallet.provider.disconnect();
        }
    }
    location.reload();
  }

  getWeb3Instance(uuid) {
    return this.web3Instances.get(uuid);
  }

  getCurrentWeb3() {
    if (!this.currentWallet) return null;
    return this.web3Instances.get(this.currentWallet.uuid);
  }
}

export const walletManager = new WalletManager();
